// _Components/AuthContext.js

import React, { useContext, useEffect, useState, useMemo, useCallback } from 'react';
import { auth, db } from '../Firebase';
import {
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    signOut,
    sendPasswordResetEmail,
    updateEmail,
    updatePassword,
} from "firebase/auth";
import { setDoc, doc } from "firebase/firestore";

const AuthContext = React.createContext();

export function useAuth() {
    return useContext(AuthContext);
}

export function AuthProvider({ children }) {
    const [currentUser, setCurrentUser] = useState(null); // تحديث القيمة الأولية إلى null
    const [loading, setLoading] = useState(true);

    // استخدام useCallback لثبات الدوال
    const signup = useCallback(async (email, password) => {
        try {
            const userCredential = await createUserWithEmailAndPassword(auth, email, password);
            const user = userCredential.user;

            // استخدام مجموعة واحدة لتخزين بيانات المستخدم
            await setDoc(doc(db, "users", user.uid), {
                email: user.email,
                uid: user.uid,
                watchlist: [], // إضافة قائمة المراقبة هنا مباشرة
            });

            console.log("Signup successful for user:", user.uid);
            return user;
        } catch (error) {
            console.error("Failed to sign up:", error);
            throw error;
        }
    }, []);

    const login = useCallback(async (email, password) => {
        try {
            return await signInWithEmailAndPassword(auth, email, password);
        } catch (error) {
            console.error("Failed to log in:", error);
            throw error;
        }
    }, []);

    const logout = useCallback(async () => {
        try {
            return await signOut(auth);
        } catch (error) {
            console.error("Failed to log out:", error);
            throw error;
        }
    }, []);

    const resetPassword = useCallback(async (email) => {
        try {
            return await sendPasswordResetEmail(auth, email);
        } catch (error) {
            console.error("Failed to send password reset email:", error);
            throw error;
        }
    }, []);

    const updateUserEmail = useCallback(async (email) => {
        try {
            return await updateEmail(currentUser, email);
        } catch (error) {
            console.error("Failed to update email:", error);
            throw error;
        }
    }, [currentUser]);

    const updateUserPassword = useCallback(async (password) => {
        try {
            return await updatePassword(currentUser, password);
        } catch (error) {
            console.error("Failed to update password:", error);
            throw error;
        }
    }, [currentUser]);

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged(user => {
            setCurrentUser(user);
            setLoading(false);
        });
        return unsubscribe;
    }, []);

    // استخدام useMemo لتحسين أداء Provider
    const value = useMemo(() => ({
        currentUser,
        login,
        signup,
        logout,
        resetPassword,
        updateUserEmail,
        updateUserPassword
    }), [
        currentUser,
        login,
        signup,
        logout,
        resetPassword,
        updateUserEmail,
        updateUserPassword
    ]);

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
}
